import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Mail, Lock, Eye, EyeOff, AlertCircle, Loader2, ShieldCheck, ChevronLeft, ArrowRight, Fingerprint, TrendingUp } from 'lucide-react';

export default function Login() {
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [passcode, setPasscode] = useState('');
  const [step, setStep] = useState(1);
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const { login, verifyPasscode } = useAuth();
  const navigate = useNavigate();

  async function handleFirstStep(e) {
    e.preventDefault();
    try {
      setError('');
      setLoading(true);
      const res = await login(identifier, password);
      if (res.userData?.role === 'admin') {
        navigate('/admin');
      } else {
        setStep(2);
      }
    } catch (err) {
      if (err.message.includes('auth/user-not-found') || err.message.includes('auth/wrong-password') || err.message.includes('auth/invalid-credential')) {
        setError('Incorrect credentials. Please check your email/phone and password.');
      } else {
        setError(err.message || 'Login failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }

  async function handlePasscodeVerify(e) {
    e.preventDefault();
    if (passcode.length !== 4) return;
    try {
      setLoading(true);
      setError('');
      if (verifyPasscode(passcode)) {
        navigate('/dashboard');
      } else {
        setError('Incorrect security PIN. Please try again.');
        setPasscode('');
      }
    } catch (err) {
      setError('System verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="h-screen flex overflow-hidden">
      {/* Left Panel — Branding (Fixed) */}
      <div className="hidden lg:flex lg:w-1/2 bg-stripe-dark relative overflow-hidden flex-col justify-between p-14 shrink-0 h-full">
        {/* Background texture */}
        <div className="absolute inset-0 opacity-[0.03]"
          style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '32px 32px' }} />
        <div className="absolute top-0 right-0 w-96 h-96 bg-primary-500/20 rounded-full -mr-48 -mt-48 blur-3xl" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-stripe-blue/10 rounded-full -ml-32 -mb-32 blur-2xl" />

        {/* Logo */}
        <div className="relative z-10">
          <img src="/assets/images/finovalogoor.png" alt="Finova" className="h-10 w-auto object-contain brightness-0 invert" />
        </div>

        {/* Center Content */}
        <div className="relative z-10 space-y-8">
          <div className="w-16 h-16 bg-primary-500/10 border border-primary-500/20 rounded-2xl flex items-center justify-center">
            <TrendingUp size={32} className="text-primary-400" />
          </div>
          <div>
            <h2 className="text-4xl font-black text-white tracking-tighter leading-tight mb-4">
              Finance made easy<br />for your modern<br />everyday needs.
            </h2>
            <p className="text-stripe-light text-base font-medium leading-relaxed">
              Experience the easiest way to manage your credit and get funds instantly with Finova.
            </p>
          </div>
          {/* Easy Finance badges */}
          <div className="flex flex-col gap-3">
            {['Instant Approval Process', 'Zero Hidden Fees Policy', 'Finance Easy For Everyone'].map(item => (
              <div key={item} className="flex items-center gap-3 text-sm text-white/60 font-medium">
                <div className="w-5 h-5 rounded-full bg-green-500/20 flex items-center justify-center shrink-0">
                  <div className="w-1.5 h-1.5 rounded-full bg-green-400" />
                </div>
                {item}
              </div>
            ))}
          </div>
        </div>

        {/* Bottom text */}
        <p className="text-white/20 text-[10px] font-bold uppercase tracking-[0.3em] relative z-10">
          © {new Date().getFullYear()} Finova (Pvt) Ltd
        </p>
      </div>

      {/* Right Panel — Form (Scrollable) */}
      <div className="flex-1 h-full overflow-y-auto flex flex-col items-center justify-center p-6 lg:p-14 lg:py-24 bg-[#f6f9fc] relative">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-primary-500/5 rounded-full -mr-64 -mt-64 blur-3xl pointer-events-none" />

        <div className="w-full max-w-md space-y-8 relative">
          {/* Mobile logo */}
          <div className="lg:hidden text-center mb-2">
            <img src="/assets/images/finovalogoor.png" alt="Finova" className="h-9 w-auto object-contain mx-auto" />
          </div>

          {/* Header */}
          <div className="text-left">
            <p className="text-[10px] font-black text-primary-500 uppercase tracking-[0.25em] mb-2">
              {step === 1 ? 'Welcome back' : 'Two-factor auth'}
            </p>
            <h1 className="text-4xl font-black text-stripe-dark tracking-tighter">
              {step === 1 ? 'Sign in' : 'Security PIN'}
            </h1>
            <p className="text-stripe-slate text-sm mt-2 font-medium">
              {step === 1
                ? 'Enter your credentials to access your account.'
                : 'Enter your 4-digit security passcode to continue.'}
            </p>
          </div>

          {/* Error */}
          {error && (
            <div className="p-4 bg-red-50 border border-red-200/80 rounded-2xl flex items-start gap-3 animate-in slide-in-from-top-2 duration-300">
              <AlertCircle size={18} className="text-red-500 mt-0.5 shrink-0" />
              <p className="text-sm text-red-600 font-semibold">{error}</p>
            </div>
          )}

          {/* Card */}
          <div className="bg-white rounded-[28px] border border-stripe-border shadow-stripe p-8 space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
            {step === 1 ? (
              <form onSubmit={handleFirstStep} className="space-y-5">
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-stripe-slate uppercase tracking-widest block">Email or Phone</label>
                  <div className="relative">
                    <Mail size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-stripe-light" />
                    <input
                      type="text"
                      required
                      className="s-input !pl-11 !py-3.5 !rounded-xl font-medium"
                      placeholder="you@example.com or +947..."
                      value={identifier}
                      onChange={(e) => setIdentifier(e.target.value)}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="text-[10px] font-black text-stripe-slate uppercase tracking-widest">Password</label>
                    <Link to="#" className="text-[10px] font-black text-primary-500 hover:text-primary-600 uppercase tracking-widest">Forgot?</Link>
                  </div>
                  <div className="relative">
                    <Lock size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-stripe-light" />
                    <input
                      type={showPass ? 'text' : 'password'}
                      required
                      className="s-input !pl-11 !pr-12 !py-3.5 !rounded-xl font-medium"
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPass(!showPass)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-stripe-light hover:text-stripe-dark transition-colors"
                    >
                      {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3.5 bg-stripe-dark text-white font-black text-sm rounded-xl hover:bg-black transition-all flex items-center justify-center gap-3 disabled:opacity-50 mt-2 group"
                >
                  {loading ? <Loader2 size={18} className="animate-spin" /> : (
                    <>Continue <ArrowRight size={16} className="group-hover:translate-x-0.5 transition-transform" /></>
                  )}
                </button>
              </form>
            ) : (
              <form onSubmit={handlePasscodeVerify} className="space-y-6">
                <div className="text-center space-y-5">
                  <div className="inline-flex items-center justify-center w-14 h-14 bg-primary-50 text-primary-500 rounded-2xl border border-primary-100">
                    <Fingerprint size={28} />
                  </div>
                  <div className="space-y-3">
                    <label className="text-[10px] font-black text-stripe-slate uppercase tracking-widest block">4-Digit PIN</label>
                    <input
                      type="password"
                      maxLength={4}
                      autoFocus
                      required
                      className="s-input !py-5 text-center text-4xl font-black tracking-[1em] placeholder:text-stripe-border !rounded-xl"
                      placeholder="0000"
                      value={passcode}
                      onChange={(e) => setPasscode(e.target.value.replace(/[^0-9]/g, ''))}
                    />
                  </div>
                </div>

                <div className="space-y-3">
                  <button
                    type="submit"
                    disabled={loading || passcode.length !== 4}
                    className="w-full py-3.5 bg-stripe-dark text-white font-black text-sm rounded-xl hover:bg-black transition-all flex items-center justify-center gap-3 disabled:opacity-40"
                  >
                    {loading ? <Loader2 size={18} className="animate-spin" /> : 'Unlock Account'}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setStep(1); setPasscode(''); setError(''); }}
                    className="w-full flex items-center justify-center gap-2 text-[10px] font-black text-stripe-slate hover:text-stripe-dark uppercase tracking-widest py-2 transition-colors"
                    disabled={loading}
                  >
                    <ChevronLeft size={14} /> Back to Sign In
                  </button>
                </div>
              </form>
            )}
          </div>

          {/* Footer link */}
          <p className="text-center text-sm text-stripe-slate font-medium">
            New to Finova?{' '}
            <Link to="/register" className="font-black text-primary-500 hover:text-primary-600 transition-colors">
              Create an account
            </Link>
          </p>


        </div>
      </div>
    </div>
  );
}
